import { useEffect } from 'react';
import { Flex, Spin } from 'antd';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useGetMembershipsQuery } from '@/entities/user';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { storeSelected } from '@/shared/lib/redux/actions';
import { StoreContextProvider } from '@/shared/lib/store-context';
import { ROUTES } from '@/shared/config/routes';
import { useBreakpoint } from '@/shared/lib/hooks';
import { fullScreenMinHeight } from '@/shared/lib/mobileUi';

function Loading() {
  const { isMobile } = useBreakpoint();
  return (
    <Flex align="center" justify="center" style={{ minHeight: fullScreenMinHeight(isMobile) }}>
      <Spin size="large" />
    </Flex>
  );
}

/**
 * Resolves `:storeId` from the URL into `X-Store-Id` (baseApi reads it from Redux, §1) and a
 * `StoreContext` (storeName/timezone/role) for the routed subtree. Waits for the dispatch to land
 * before rendering children, so no store-scoped query ever fires with a stale/missing header.
 */
export function StoreScope() {
  const { storeId } = useParams<{ storeId: string }>();
  const dispatch = useAppDispatch();
  const currentStoreId = useAppSelector((state) => state.storeContext.currentStoreId);
  const { data: memberships, isLoading } = useGetMembershipsQuery();

  useEffect(() => {
    if (storeId && storeId !== currentStoreId) dispatch(storeSelected(storeId));
  }, [storeId, currentStoreId, dispatch]);

  if (!storeId) return <Navigate to={ROUTES.stores} replace />;
  if (isLoading || currentStoreId !== storeId) return <Loading />;

  const membership = memberships?.find((m) => m.storeId === storeId);
  if (!membership || membership.status !== 'APPROVED') {
    return <Navigate to={ROUTES.stores} replace />;
  }

  return (
    <StoreContextProvider
      value={{ storeId, storeName: membership.storeName, timezone: membership.timezone, role: membership.role }}
    >
      <Outlet />
    </StoreContextProvider>
  );
}
